import { goRequest } from '../utils/httpClientTs';

export interface EmailCampaign {
  id: string;
  name: string;
  status: string;
  template_id?: string | null;
  subject?: string | null;
  audience_ids?: string;
  audience_names?: string | null;
  estimated_recipient_count?: number | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  created_at: string;
  created_by_id?: string | null;
  recipient_count?: number | null;
  sent_count?: number | null;
  delivered_count?: number | null;
  opened_count?: number | null;
  clicked_count?: number | null;
  bounce_count?: number | null;
  last_stats_at?: string | null;
}

export interface EmailCampaignListItem {
  id: string;
  name: string;
  status: string;
  template_id?: string | null;
  subject?: string | null;
  audience_ids?: string;
  audience_names?: string | null;
  estimated_recipient_count?: number | null;
  scheduled_at?: string | null;
  sent_at?: string | null;
  created_at: string;
}

export interface CreateEmailCampaignRequest {
  name: string;
  template_id?: string;
  subject?: string;
  audience_ids: string[];
}

export interface UpdateEmailCampaignRequest {
  name?: string;
  template_id?: string;
  subject?: string;
  audience_ids?: string[];
}

export interface ScheduleEmailCampaignRequest {
  scheduled_at: string; // ISO datetime
}

export interface ListEmailCampaignsParams {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
}

export interface ListEmailCampaignsResult {
  items: EmailCampaignListItem[];
  total_count: number;
  page: number;
  per_page: number;
}

export interface Audience {
  id: string;
  name: string;
}

export const getEmailCampaigns = (
  params?: ListEmailCampaignsParams
): Promise<ListEmailCampaignsResult> => {
  const searchParams = new URLSearchParams();
  if (params?.page != null) searchParams.set('page', String(params.page));
  if (params?.per_page != null)
    searchParams.set('per_page', String(params.per_page));
  if (params?.status) searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.start_date) searchParams.set('start_date', params.start_date);
  if (params?.end_date) searchParams.set('end_date', params.end_date);
  const qs = searchParams.toString();
  return goRequest<ListEmailCampaignsResult>({
    url: `/api/email-campaigns${qs ? `?${qs}` : ''}`,
    method: 'GET',
  });
};

export const getEmailCampaign = (id: string): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}`,
    method: 'GET',
  });
};

export const createEmailCampaign = (
  data: CreateEmailCampaignRequest
): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: '/api/email-campaigns',
    method: 'POST',
    data,
  });
};

export const updateEmailCampaign = (
  id: string,
  data: UpdateEmailCampaignRequest
): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}`,
    method: 'PATCH',
    data,
  });
};

export const scheduleEmailCampaign = (
  id: string,
  data: ScheduleEmailCampaignRequest
): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}/schedule`,
    method: 'POST',
    data,
  });
};

export const cancelEmailCampaign = (id: string): Promise<EmailCampaign> => {
  return goRequest<EmailCampaign>({
    url: `/api/email-campaigns/${id}/cancel`,
    method: 'POST',
  });
};

export const deleteEmailCampaign = (id: string): Promise<void> => {
  return goRequest<void>({
    url: `/api/email-campaigns/${id}`,
    method: 'DELETE',
  });
};

export const getEmailCampaignAudiences = (): Promise<Audience[]> => {
  return goRequest<Audience[]>({
    url: '/api/email-campaigns/audiences',
    method: 'GET',
  });
};
